<template>
  <v-card border class="comment-item" flat rounded="lg">
    <div v-if="!isEditing" class="d-flex ga-3">
      <user-avatar :user="comment.user"></user-avatar>

      <div class="flex-grow-1 overflow-hidden">
        <div class="d-flex align-center flex-wrap column-gap-2">
          <span class="font-weight-bold font-size-15">
            {{ getPersonName(comment.user, { initials: true }) }}
          </span>
          <span v-if="comment.user.post" class="text-disabled font-size-13">
            {{ comment.user.post }}
          </span>
          <span class="text-disabled font-size-13">
            · {{ new CustomDate(comment.created_at).toString({ time: true }) }}
          </span>
        </div>

        <pre class="comment-item__text">{{ comment.comment }}</pre>

        <div
          v-if="comment.documents?.length"
          class="d-flex flex-column ga-2 mt-2">
          <div
            v-for="file in comment.documents"
            :key="file.path"
            class="comment-item__file d-flex align-center ga-2">
            <v-icon
              color="blue-darken-3"
              icon="mdi-file-outline"
              size="18"></v-icon>
            <span
              class="comment-item__file-name flex-grow-1 cursor-pointer"
              @click="openDocument(file.path)">
              {{ file.name }}
            </span>
            <v-btn
              v-tooltip="'Завантажити'"
              color="blue-darken-3"
              density="comfortable"
              icon="mdi-download"
              size="x-small"
              variant="text"
              @click="download(file.path, file.name)"></v-btn>
          </div>
        </div>

        <div v-if="!disabled" class="d-flex ga-4 mt-1">
          <v-btn
            color="light-blue-darken-4"
            class="px-1"
            density="comfortable"
            variant="text"
            @click="startEdit">
            <v-icon class="mr-1" icon="mdi-pencil-outline" size="18"></v-icon>
            <span>Редагувати</span>
          </v-btn>
          <v-btn
            :loading="$loading.isLoading('commentDelete')"
            class="px-1"
            color="error"
            density="comfortable"
            variant="text"
            @click="commentDelete(comment.id)">
            <v-icon
              class="mr-1"
              icon="mdi-trash-can-outline"
              size="18"></v-icon>
            <span>Видалити</span>
          </v-btn>
        </div>
      </div>
    </div>

    <div v-else>
      <b-textarea
        v-model="editText"
        class="bg-white mb-3"
        hide-details
        rows="3"></b-textarea>
      <div class="d-flex justify-end ga-2">
        <v-btn
          color="white"
          elevation="1"
          style="border: 1px solid #e4e4e7"
          variant="flat"
          @click="cancelEdit">
          Скасувати
        </v-btn>
        <v-btn
          :disabled="!editText?.trim()"
          :loading="$loading.isLoading('commentUpdate')"
          @click="saveEdit">
          Зберегти
        </v-btn>
      </div>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { Comment } from '@/utils/types/comment'
import UserAvatar from '@/components/UserAvatar.vue'
import { BTextarea } from 'best-modules/components'
import { CustomDate, getPersonName } from 'best-modules/utils'
import { download, openDocument } from 'best-modules/vue-utils'
import { useComments } from './index'

defineOptions({
  name: 'CommentItem',
})

const props = defineProps<{
  comment: Comment
  disabled?: boolean
}>()

const { commentDelete, commentUpdate } = useComments()

const isEditing = ref<boolean>(false)
const editText = ref<string>('')

const startEdit = () => {
  editText.value = props.comment.comment
  isEditing.value = true
}

const cancelEdit = () => {
  isEditing.value = false
  editText.value = ''
}

const saveEdit = () => {
  const value = editText.value.trim()
  if (!value) return

  return commentUpdate(props.comment.id, value).then(() => {
    isEditing.value = false
  })
}
</script>

<style lang="scss" scoped>
.comment-item {
  padding: 16px;
}

.comment-item__text {
  margin: 4px 0 0;
  font-family: inherit;
  font-size: 14px;
  white-space: pre-wrap;
  word-break: break-word;
}

.column-gap-2 {
  column-gap: 8px;
}

.comment-item__file {
  max-width: 420px;
  padding: 4px 10px;
  border: 1px solid #e5e7ef;
  border-radius: 8px;
}

.comment-item__file-name {
  overflow: hidden;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
